import { createSelector } from "@reduxjs/toolkit";

import { resolveActiveNodesSelector } from "../nodes/querySelectors/index.ts";
import {
  getDesignerNodeByIdSelector,
  getManifestSelector,
  getParentContextByIdSelector,
  getValueSubtypeDefinitionSelector,
} from "../state/index.ts";
import { resolveValueExpressionContextCreate } from "./expressions/resolveValueExpressionContext.ts";
import { getNodeValueCreate } from "./querying/getNodeValue.ts";
import { getNodeValuesCreate } from "./querying/getNodeValues.ts";
import { resolveAvailableValuesCreate } from "./querying/resolveAvailableValues.ts";
import { resolveDesignTimeValueCreate } from "./resolveDesignTimeValue.ts";
import { resolveValueToConditionValueCreate, resolveValueToRuntimeDynamicValueCreate } from "./resolveValueConfigs.ts";
import { resolveValueSignatureCreate } from "./resolveValueSignature.ts";

/**
 * Value resolution over designer state, as one chain of selectors.
 *
 * Every reader of a configured value stands on the same few steps — what values
 * a node advertises, then one of them, then what it resolves to — so they are
 * built once here and composed, rather than reassembled at each call site. Each
 * one re-derives only when the state it reads changes, and everything downstream
 * of it holds still, which is what keeps a form re-reading its subject cheap.
 *
 * These are the plain selectors; the `use*` hooks beside them are the React
 * surface, and a consumer outside a component (a context provider assembling its
 * value) reads them straight.
 */
export const getNodeValuesSelector = createSelector(
  [getManifestSelector, getParentContextByIdSelector],
  (getManifest, getParentContext) => getNodeValuesCreate({ getManifest, getParentContext }),
);

export const getNodeValueSelector = createSelector(
  [getDesignerNodeByIdSelector, getNodeValuesSelector],
  (getById, getNodeValues) => getNodeValueCreate({ getById, getNodeValues }),
);

/** The values in scope of a node, by its id. */
export const resolveAvailableValuesSelector = createSelector(
  [resolveActiveNodesSelector, getNodeValuesSelector],
  (resolveActiveNodes, getNodeValues) => resolveAvailableValuesCreate({ resolveActiveNodes, getNodeValues }),
);

export const resolveDesignTimeValueSelector = createSelector([getNodeValueSelector], (getNodeValue) =>
  resolveDesignTimeValueCreate({ getNodeValue }),
);

export const resolveValueSignatureSelector = createSelector([getNodeValueSelector], (getNodeValue) =>
  resolveValueSignatureCreate({ getNodeValue }),
);

export const resolveValueToConditionValueSelector = createSelector(
  [getNodeValueSelector, resolveDesignTimeValueSelector],
  (getNodeValue, resolveDesignTimeValue) =>
    resolveValueToConditionValueCreate({ getNodeValue, resolveDesignTimeValue }),
);

/** What an expression over a picked subject needs to build, gathered from the subject. */
export const resolveValueExpressionContextSelector = createSelector(
  [resolveValueSignatureSelector, getValueSubtypeDefinitionSelector, resolveValueToConditionValueSelector],
  (resolveValueSignature, getValueSubtypeDefinition, resolveValue) =>
    resolveValueExpressionContextCreate({ resolveValueSignature, getValueSubtypeDefinition, resolveValue }),
);

export const resolveValueToRuntimeDynamicValueSelector = createSelector(
  [getNodeValueSelector, resolveDesignTimeValueSelector, getValueSubtypeDefinitionSelector],
  (getNodeValue, resolveDesignTimeValue, getValueSubtypeDefinition) =>
    resolveValueToRuntimeDynamicValueCreate({ getNodeValue, resolveDesignTimeValue, getValueSubtypeDefinition }),
);
